import { MenuItem } from "@/model/entities";
import { MenuItemFormData } from "@/components/EnhancedMenuItemDialogForm.stateless";
import { menuItemService } from "@/service/menuItemService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useMenuItems(categoryId?: string) {
  const qc = useQueryClient();
  const query = useQuery<MenuItem[]>({
    queryKey: ["menuItems", categoryId],
    queryFn: () => menuItemService.fetchAllByCategoryId(categoryId!),
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const addMenuItem = useMutation({
    mutationFn: (newItem: MenuItem) => menuItemService.create(newItem),
    onSuccess: async () => {
      // Invalidate all menu items queries (category-specific and restaurant-wide)
      await qc.invalidateQueries({ queryKey: ["menuItems"] });
    },
  });

  const updateMenuItem = useMutation({
    mutationFn: (updatedItem: MenuItem) => {
      if (!updatedItem.id) throw new Error("Menu item id is required");
      return menuItemService.update(updatedItem.id, updatedItem);
    },
    onSuccess: async () => {
      // Invalidate all menu items queries (category-specific and restaurant-wide)
      await qc.invalidateQueries({ queryKey: ["menuItems"] });
    },
  });

  const updateBulkMenuItems = useMutation({
    mutationFn: (items: MenuItem[]) => menuItemService.updateMany(items),
    onSuccess: () => {
      query.refetch();
    },
  });

  const deleteMenuItem = useMutation({
    mutationFn: (id: string) => menuItemService.delete(id),
    onSuccess: async () => {
      // Invalidate all menu items queries (category-specific and restaurant-wide)
      await qc.invalidateQueries({ queryKey: ["menuItems"] });
    },
  });

  const toggleAvailability = useMutation({
    mutationFn: async ({
      itemId,
      isAvailable,
    }: {
      itemId: string;
      isAvailable: boolean;
    }) => {
      if (!itemId) throw new Error("Menu item id is required");
      return menuItemService.toggleAvailability(itemId, isAvailable);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["menuItems"] });
    },
  });

  return {
    ...query,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    updateBulkMenuItems,
    toggleAvailability,
  };
}

export function useMenuItemsByRestaurant(restaurantId: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["menuItems", restaurantId],
    queryFn: () => menuItemService.fetchAllByRestaurantId(restaurantId!),
    enabled: !!restaurantId,
  });

  const addMenuItem = useMutation({
    mutationFn: (newItem: MenuItemFormData) =>
      menuItemService.createMenuItem(restaurantId, newItem),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["menuItems", restaurantId] });
    },
  });

  const deleteMenuItem = useMutation({
    mutationFn: (id: string) => menuItemService.delete(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["menuItems", restaurantId] });
    },
  });

  const updateMenuItem = useMutation({
    mutationFn: ({
      id,
      updatedItem,
    }: {
      id: string;
      updatedItem: MenuItemFormData;
    }) => {
      if (!id) throw new Error("Menu item id is required");
      return menuItemService.updateMenuItem(restaurantId, id, updatedItem);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["menuItems", restaurantId] });
    },
  });

  const updateBulkMenuItems = useMutation({
    mutationFn: (items: MenuItem[]) => menuItemService.updateMany(items),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["menuItems", restaurantId] });
    },
  });

  const toggleAvailability = useMutation({
    mutationFn: async ({
      itemId,
      isAvailable,
    }: {
      itemId: string;
      isAvailable: boolean;
    }) => {
      if (!itemId) throw new Error("Menu item id is required");
      return menuItemService.toggleAvailability(itemId, isAvailable);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["menuItems", restaurantId] });
    },
  });

  return {
    ...query,
    deleteMenuItem,
    updateMenuItem,
    addMenuItem,
    updateBulkMenuItems,
    toggleAvailability,
  };
}
